import { X } from "lucide-react";

interface Props {
  onClose: () => void;
  onLearnMore: () => void;
}

/**
 * First-open "what am I looking at?" decoder for the Landscape's ~9 visual
 * encodings. Show-once (gated by a localStorage flag in LandscapeOverlay) with
 * a persistent "?" affordance in the header to reopen. Anchored top-left inside
 * the canvas so it doesn't block the sidebar or the densest center of the map.
 */
export function LandscapeOrientationCard({ onClose, onLearnMore }: Props) {
  return (
    <div className="absolute left-3 top-3 z-10 w-[280px] rounded-lg border border-gray-200 bg-white/95 p-3 shadow-lg backdrop-blur-sm dark:border-gray-700 dark:bg-gray-800/95">
      <div className="mb-2 flex items-start justify-between gap-2">
        <h3 className="text-xs font-semibold text-gray-900 dark:text-gray-100">
          What am I looking at?
        </h3>
        <button
          type="button"
          onClick={onClose}
          aria-label="Dismiss"
          className="-mr-1 -mt-0.5 shrink-0 text-gray-400 hover:text-gray-600 dark:hover:text-gray-200"
        >
          <X className="size-3.5" />
        </button>
      </div>

      <ul className="space-y-1.5 text-[11px] leading-relaxed text-gray-600 dark:text-gray-300">
        <li>
          <span className="font-medium text-gray-800 dark:text-gray-100">
            Each dot is a paper.
          </span>{" "}
          Closer dots are more similar — the map is built from semantic
          embeddings, so proximity is meaning.
        </li>
        <li>
          <span className="font-medium text-gray-800 dark:text-gray-100">
            Color
          </span>{" "}
          is argumentation role, or topic cluster — switch with{" "}
          <span className="italic">Color by</span> in the sidebar.
        </li>
        <li>
          <span className="font-medium text-gray-800 dark:text-gray-100">
            Size
          </span>{" "}
          is relevance to your concept; faded dots are unread.
        </li>
        <li>
          <span className="font-medium text-gray-800 dark:text-gray-100">
            A ring
          </span>{" "}
          marks a paper that bridges two camps; a white center means it has
          evidence attached.
        </li>
        <li>
          <span className="font-medium text-gray-800 dark:text-gray-100">
            Soft regions
          </span>{" "}
          are debate camps; the amber beacon is your own argument.
        </li>
      </ul>

      <button
        type="button"
        onClick={onLearnMore}
        className="mt-2.5 text-[11px] font-medium text-purple-600 underline decoration-purple-400 decoration-dotted underline-offset-2 hover:text-purple-800 dark:text-purple-400"
      >
        Learn more about the Landscape
      </button>
    </div>
  );
}
